import {
    AfterViewInit, ApplicationRef, ChangeDetectionStrategy, Component, ComponentRef, ElementRef, Injector, signal, viewChild,
    OnChanges, OnDestroy, SimpleChanges, TemplateRef, booleanAttribute, contentChild, createComponent, inject, input, output,
} from '@angular/core';
import { Router } from '@angular/router';

import { Editor } from '@tiptap/core';
import { Code } from '@tiptap/extension-code';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import { TableOfContentData, TableOfContents, getHierarchicalIndexes } from '@tiptap/extension-table-of-contents';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import { BackgroundColor, Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';

import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ClipboardService } from 'ngx-clipboard';

import { LANGS, crEl, getUuid, getYMD, saveBlobAs, replaceTreeArray, traverseTreeArray } from '@models';
import { RusPipe } from '@pipes/rus.pipe';

import { VzCodeBlockAsync } from './codeblock';
import { VzEmoji } from './emojis/emoji';
import { VzImage } from './image/image';
import { ImageViewComponent } from './image/image-view.component';
import { Link } from './link/link';
import { Mention } from './mentions/mention';
import { MenitonViewComponent } from './mentions/mention-view.component';
import { ColorTableCell } from './tables/table-cell';
import { TemplateVariable } from './tpl-vars/tpl-var';
import { VzInfoBlock } from './info-block/info-block';
import { showFilePreview } from '@utils/files';
import { PublicApiService } from '@utils/api.service';
import { BaseMin } from '@utils/base-min';

export enum ViewerUserEventType {
    MentionClick        = 1
}

export interface ViewerUserEvent {
    type: ViewerUserEventType;
    data: any;
}

const RUS = new RusPipe().transform;

@Component({
    selector: 'vz-editor-viewer, [vz-editor-viewer]',
    template: `
        @if (editorContentWasInvalid(); as reason) {
            <div class="txt-err ph05 pv025 tac bg-shade05-err pe-none">
                Текст содержит неподдерживаемые элементы, они преобразованы в текст или скрыты. Для корректной работы обновите приложение.
            </div>
        }

        @if (headerTpl(); as tpl) {
            <ng-container [ngTemplateOutlet]="tpl" />
        }
        <div #editorDiv></div>
        @if (footerTpl(); as tpl) {
            <ng-container [ngTemplateOutlet]="tpl" />
        }
    `,
    styleUrls: ['./viewer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditorViewerComponent extends BaseMin implements AfterViewInit, OnChanges, OnDestroy {

    readonly editorDiv  = viewChild.required<ElementRef>('editorDiv');
    readonly headerTpl  = contentChild('headerTpl', { read: TemplateRef });
    readonly footerTpl  = contentChild('footerTpl', { read: TemplateRef });

    readonly value      = input<any>();
    readonly withToc    = input<boolean, unknown>(false, { transform: booleanAttribute });
    readonly public     = input<boolean, unknown>(false, { transform: booleanAttribute });
    readonly type       = input<'task' | 'chat' | 'wiki'>();

    readonly toc        = output<TableOfContentData | undefined>();
    readonly userEvent  = output<ViewerUserEvent>();

    editor!: Editor;
    readonly editorContentWasInvalid    = signal<boolean>(false);

    cm?: {
        event: Event,
        user?: {
            id: string,
            name: string,
            active: boolean,
        },
        chat?: {
            id: string,
            trid?: string,
            title: string,
        },
        task?: {
            id: string,
            title: string,
        },
        wiki?: {
            id: string,
            title: string,
        },
        url?: string,
    };

    private readonly _injector      = inject(Injector);
    private readonly _api           = inject(PublicApiService);
    private readonly _router        = inject(Router);
    private readonly _notification  = inject(NzNotificationService);
    private readonly _cb            = inject(ClipboardService);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value && this.editor) {
            if (!changes.value.firstChange) {
                this.createEditor();
            }
        }
    }

    ngAfterViewInit(): void {
        this.createEditor();
    }

    ngOnDestroy(): void {
        this.editor?.destroy();
    }

    createEditor(): void {
        this.editor?.destroy();
        this.editorDiv().nativeElement?.scroll(0, 0);

        const extensions = [
            (StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                hardBreak: false,
                codeBlock: false,
                code: false,
                link: false
            }) as any),
            Code.extend({ excludes: 'italic strike underline subscript superscript' }),
            Subscript,
            Superscript,
            Highlight,
            TaskList, TaskItem,
            Color, BackgroundColor, TextStyle, FontSize,
            TableKit.configure({
                tableCell: false,
            }),
            ColorTableCell,
            VzImage.configure({
                cmpRef: {},
                renderLabel: ({ options, node }) => {
                    if (!node.attrs.sid) {
                        (node.attrs as any).sid = getUuid().substring(0, 8);
                    }
                    if (!options.cmpRef[node.attrs.sid]) {
                        const appRef = this._injector.get(ApplicationRef);
                        options.cmpRef[node.attrs.sid] = createComponent(ImageViewComponent, { environmentInjector: appRef.injector });
                        (options.cmpRef[node.attrs.sid] as ComponentRef<ImageViewComponent>).instance.previewClick.subscribe(v => {
                            if (v?.f) {
                                showFilePreview(v.f, [v.f], this._injector);
                            }
                        });
                    }
                    const cmpRef: ComponentRef<ImageViewComponent> = options.cmpRef[node.attrs.sid];
                    cmpRef.setInput('fileId', node.attrs.fileId);
                    cmpRef.setInput('clickable', true);
                    cmpRef.changeDetectorRef.detectChanges();
                    return cmpRef.injector.get(ElementRef).nativeElement as any;
                },
                onDestroy: (options) => {
                    Object.values(options.cmpRef).forEach((cr: any) => cr.destroy());
                    options.cmpRef = undefined;
                }
            }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure(),
            VzCodeBlockAsync.configure({
                id: getUuid().substring(0, 8),
                cmpRef: {},
                hlWorker: this._store.hlWorker,
                hlWorkerMessage: this._store.hlWorkerMessage,
                renderHeader: ({ options, node }) => {
                    const linesCount = (node.textContent || '').split(/\r\n|\r|\n/).length;
                    const snippetLength = (node.textContent || '').length;

                    const root = crEl(
                        'div',
                        'snippet-viewer-wrapper' + (linesCount > 5 || snippetLength > 1500 ? ' svw-collapsed' : ''),
                        { 'margin-bottom': `${linesCount > 5 || snippetLength > 1500 ? '12' : '8'}px`, 'margin-top': '16px' }
                    );

                    const divEditor = crEl('div', 'svw-editor');
                    const pre = crEl('pre');
                    const code = crEl('code', node.attrs.language ? options.languageClassPrefix + node.attrs.language : undefined);
                    pre.setAttribute('data-sid', getUuid().substring(0, 8));
                    pre.append(code);
                    divEditor.append(pre);

                    root.append(divEditor);

                    const header = crEl('div', 'svw-header layout', { width: 'calc(100% - 50px)' });
                    let footer = undefined;
                    if (linesCount > 5 || snippetLength > 1500) {
                        const btnCollapse = crEl('button', 'ant-btn h3-btn bc b1s ant-btn-icon-only top-2 mr1 ant-btn-text ant-btn-circle ant-btn-sm');
                        const btnCollapseFooter = crEl('button', 'hidden ant-btn h3-btn bc b1s ant-btn-icon-only ant-btn-text ant-btn-circle ant-btn-sm');
                        btnCollapse.title = 'Развернуть';
                        const btnCollapseIcon = crEl('i', 'fs-xxl top-1 vzi-ch-right');
                        btnCollapse.append(btnCollapseIcon);
                        let collapsed = true;
                        btnCollapse.onclick = () => {
                            collapsed = !collapsed;
                            if (collapsed) {
                                root.classList.add('svw-collapsed');
                                btnCollapseIcon.classList.add('vzi-ch-right');
                                btnCollapseIcon.classList.remove('vzi-ch-down');
                                btnCollapseFooter.classList.add('hidden');
                                btnCollapse.title = 'Развернуть';
                            }
                            else {
                                root.classList.remove('svw-collapsed');
                                btnCollapseIcon.classList.remove('vzi-ch-right');
                                btnCollapseIcon.classList.add('vzi-ch-down');
                                btnCollapseFooter.classList.remove('hidden');
                                btnCollapse.title = 'Свернуть';
                            }
                        };
                        btnCollapseFooter.append(crEl('i', 'fs-xxl vzi-ch-up'));
                        btnCollapseFooter.title = 'Свернуть';
                        btnCollapseFooter.onclick = () => btnCollapse.click();
                        header.append(btnCollapse);
                        footer = crEl('div', 'svw-footer layout');
                        footer.append(btnCollapseFooter);
                        divEditor.onclick = () => {
                            if (collapsed) {
                                btnCollapse.click();
                            }
                        };
                    }
                    const btnGroup = crEl('nz-button-group' as any, 'ant-btn-group top-2 mr1 ant-btn-group-sm');

                    const btnCopy = crEl('button', 'ant-btn ant-btn-icon-only ph025 ant-btn-sm');
                    btnCopy.append(crEl('i', 'vzi-copy'));
                    btnCopy.title = 'Скопировать код';
                    btnCopy.onclick = () => this.copy2clipboard(node.textContent, 'Текст скопирован в буфер обмена');

                    const btnDownload = crEl('button', 'ant-btn ant-btn-icon-only ph025 ant-btn-sm');
                    btnDownload.append(crEl('i', 'vzi-cloud-download'));
                    btnDownload.title = 'Загрузить код как файл';
                    btnDownload.onclick = () => this.downloadText(node.textContent);

                    btnGroup.append(btnCopy, btnDownload);

                    const title = crEl('span', 'layout flex relative');
                    header.append(btnGroup, title);

                    if (!this._mobile()) {
                        const lc = crEl('span', 'vz-data-text txt-l ml1 ph025 bg br6', { 'user-select': 'none' } );
                        // lc.innerHTML = linesCount + ' строк';
                        lc.setAttribute('contenteditable', 'false');
                        lc.setAttribute('data-text', linesCount + ' строк' + RUS(linesCount, ['','а','и','и','и','']));
                        header.append(lc);
                        if (node.attrs.language) {
                            const l = crEl('span', 'vz-data-text txt-l ml1 ph025 bg br6', { 'user-select': 'none' });
                            let lang = LANGS[node.attrs.language] || node.attrs.language;
                            if (lang) {
                                const ind = lang.indexOf(' (');
                                lang = lang.substring(0, ind > 0 ? ind : undefined);
                            }
                            l.setAttribute('contenteditable', 'false');
                            l.setAttribute('data-text', lang)
                            // l.innerHTML = lang;
                            header.append(l);
                        }
                    }

                    root.append(header);
                    if (footer) {
                        root.append(footer);
                    }

                    return { dom: root, contentDOM: code };
                },
                onDestroy: (options) => {
                    // console.log(`[CB-${options.id}] - onDestroy`, 'options.cmpRef:', options.cmpRef);
                    Object.values(options.cmpRef).forEach((cr: any) => cr.destroy());
                    options.cmpRef = undefined;
                }
            }),
            VzEmoji.configure({
                renderLabel: ({ node }) => {
                    const img = document.createElement('img');
                    img.src = `./assets/emojis/${node.attrs.hexcode}.png`;
                    img.style.filter = 'drop-shadow(0px 1px 1px var(--vz-textColorLL, rgba(89, 89, 89, 0.85)))';
                    return img as any;
                }
            }),
            Mention.configure({
                cmpRef: {},
                renderLabel: ({ options, node }) => {
                    if (!node.attrs.sid) {
                        (node.attrs as any).sid = getUuid().substring(0, 8);
                    }
                    if (!options.cmpRef[node.attrs.sid]) {
                        const appRef = this._injector.get(ApplicationRef);
                        options.cmpRef[node.attrs.sid] = createComponent(MenitonViewComponent, { environmentInjector: appRef.injector });
                    }
                    const cmpRef: ComponentRef<MenitonViewComponent> = options.cmpRef[node.attrs.sid];
                    cmpRef.instance.setAttrs(node.attrs);
                    cmpRef.instance.click.subscribe(e => {
                        const id = node.attrs.id?.substring(5);
                        if (id) {
                            this._router.navigate([id]);
                        }
                    });
                    cmpRef.changeDetectorRef.detectChanges();
                    return cmpRef.injector.get(ElementRef).nativeElement as any;
                },
                onDestroy: (options) => {
                    Object.values(options.cmpRef).forEach((cr: any) => cr.destroy());
                    options.cmpRef = undefined;
                }
            }),
            TemplateVariable.configure(),
            VzInfoBlock.configure()
        ];

        if (this.withToc()) {
            extensions.push(
                TableOfContents.configure({
                    getIndex: getHierarchicalIndexes,
                    onUpdate: data => this.toc.emit(data)
                })
            );
        }

        this.editor = new Editor({
            element: this.editorDiv().nativeElement, // this._elRef.nativeElement,
            enableContentCheck: false,
            editable: false,
            extensions
        });
        // this._L('createEditor', 'editor:', this.editor);
        this.setEditorContent(this.value());
        this.editorDiv().nativeElement?.scroll(0, 0);
    }

    setEditorContent(doc: any): void {
        // this._L('setEditorContent', 'doc:', doc);
        try {
            this.editor.commands.setContent(doc, { errorOnInvalidContent: true });
        }
        catch (e) {
            const nch = this.editor.schema.nodeFromJSON;
            replaceTreeArray<any>(doc?.content || [], 'content', x => {
                let node;
                try {
                    node = nch(x)
                } catch {}
                return node ? x : { type: 'paragraph', content: [{ type: 'text', text: this.getTextFromNode(x), marks: [{ type: 'textStyle', attrs: { backgroundColor: '#ffe7e8' } }] }] } ;
            });
            this.editorContentWasInvalid.set(true);
            try {
                this.editor.commands.setContent(doc, { errorOnInvalidContent: false });
            } catch {}
        }
    }

    getTextFromNode(node: any): string {
        let res = '';
        if (node.content) {
            traverseTreeArray<any>(node.content, 'content', n => {
                if (n?.type == 'text' && n?.text) {
                    res += ' ' + n.text;
                }
            });
        }
        return res || ' ';
    }

    gotoWiki(id: string): void {
        this._router.navigate(['/ui', id]);
    }

    copy2clipboard(txt: string, msg = 'Ссылка скопирована'): void {
        this._cb.copyFromContent(txt);
        this._notification.info(msg, '', { nzClass: 'nt-info', nzDuration: 1000 });
    }

    downloadText(txt: string): void {
        const fn = 'snippet-' + getYMD(new Date()) +'.txt';
        saveBlobAs(new Blob([txt || ''], { type: 'text/plain' }), fn, false).then();
    }

}
